import React from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { ResourceContentFormat } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { LyraAISummaryResource } from '../../../data/lyraAISummariesApi'

const EssentialsSection = styled.View(({ theme }) => ({
  marginTop: theme.spacing['12px'],
}))

const EssentialsList = styled.View(({ theme }) => ({
  marginTop: theme.spacing['12px'],
  paddingLeft: theme.spacing['8px'],
}))

const EssentialsListItem = styled.View({
  flexDirection: 'row',
})

const EssentialsBullet = styled.View(({ theme }) => ({
  width: theme.spacing['16px'],
}))

const EssentialsItemContent = styled.View({
  flex: 1,
})

type MessengerSummaryEssentialsProps = {
  resources?: LyraAISummaryResource[]
}

const getResourceTypeLabel = (contentFormat: LyraAISummaryResource['content_format']) => {
  switch (contentFormat) {
    case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_ARTICLE:
    case 'RESOURCE_CONTENT_FORMAT_ARTICLE':
      return <FormattedMessage defaultMessage='Article' description='Essentials resource type in an AI summary' />
    case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_VIDEO:
    case 'RESOURCE_CONTENT_FORMAT_VIDEO':
      return <FormattedMessage defaultMessage='Video' description='Essentials resource type in an AI summary' />
    case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_AUDIO:
    case 'RESOURCE_CONTENT_FORMAT_AUDIO':
      return <FormattedMessage defaultMessage='Audio' description='Essentials resource type in an AI summary' />
    default:
      return null
  }
}

const MessengerSummaryEssentials: React.FC<MessengerSummaryEssentialsProps> = ({ resources }) => {
  const { colors } = useTheme()

  if (!resources?.length) {
    return null
  }

  return (
    <EssentialsSection testID={tID('SummaryEssentialsSection')}>
      <BodyText
        size={BodyTextSize.DEFAULT}
        color={colors.textSecondary}
        text={
          <FormattedMessage
            defaultMessage='Lyra AI shared {count, plural, one {# resource} other {# resources}} with the client:'
            description='Label introducing the Essentials resources Lyra AI shared with the client during a summarized conversation'
            values={{ count: resources.length }}
          />
        }
      />
      <EssentialsList>
        {resources.map((resource, index) => {
          const description = resource.metadata?.short_description
          const resourceTypeLabel = getResourceTypeLabel(resource.content_format)

          return (
            <EssentialsListItem key={`${resource.content_id}-${index}`} testID={tID('SummaryEssentialsItem')}>
              <EssentialsBullet>
                <BodyText size={BodyTextSize.DEFAULT} color={colors.textSecondary} text='•' />
              </EssentialsBullet>
              <EssentialsItemContent>
                <BodyText
                  size={BodyTextSize.DEFAULT}
                  color={colors.textSecondary}
                  text={
                    <>
                      <BodyText size={BodyTextSize.DEFAULT} color={colors.textSecondary} bold text={resource.title} />
                      {resourceTypeLabel && (
                        <BodyText
                          size={BodyTextSize.DEFAULT}
                          color={colors.textInactive}
                          text={
                            <>
                              {' • '}
                              {resourceTypeLabel}
                            </>
                          }
                        />
                      )}
                    </>
                  }
                />
                {!!description && (
                  <BodyText size={BodyTextSize.DEFAULT} color={colors.textSecondary} text={description} />
                )}
              </EssentialsItemContent>
            </EssentialsListItem>
          )
        })}
      </EssentialsList>
    </EssentialsSection>
  )
}

export default MessengerSummaryEssentials
